import { h } from 'preact'

/**
 * BubbleContent — renders the text body of a message.
 *
 * Props: { message, isImageOnly }
 *
 * The body HTML comes pre-rendered from the server (Rails runs markdown +
 * @mention substitution). We use dangerouslySetInnerHTML to inject it.
 * When the body is plain text (no HTML), we apply a client-side renderer
 * that handles @mention tokens and basic inline markdown.
 */
export function BubbleContent({ message, isImageOnly }) {
  if (isImageOnly) return null

  const body = message.body || message.body_html || ''
  if (!body.trim()) return null

  const html = message.body_html
    ? message.body_html          // Server pre-rendered HTML
    : renderBody(body)           // Client-side: markdown + mention render

  return (
    <div
      class="chat-message-body"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

/**
 * isImageOnlyMessage — true when the message has attachments but no text body.
 * Every attachment kind (image, video, document, generic file) renders
 * bubble-less (standalone), matching the mobile app across desktop (dock +
 * full screen) and iOS/Android. Only a text body forces the bubble.
 */
export function isImageOnlyMessage(message) {
  const atts = message.attachments ?? []
  if (atts.length === 0) return false
  const body = message.body || message.body_html || ''
  return !body.trim()
}

/**
 * renderBody — escape HTML, then render @mention tokens and basic inline markdown.
 * Order matters: escape first to prevent XSS, then apply safe transforms.
 */
function renderBody(text) {
  let s = escapeHtml(text)

  // @mention tokens: @[Name](id) → styled chip
  s = s.replace(
    /@\[([^\]]+)\]\((\d+)\)/g,
    (_, name) => `<span class="chat-mention">@${escapeHtml(name)}</span>`
  )

  // Inline markdown (no block-level elements — keep it simple and safe)
  s = s
    .replace(/\*\*(.+?)\*\*/gs,  '<strong>$1</strong>')   // **bold**
    .replace(/\*(.+?)\*/gs,      '<em>$1</em>')            // *italic*
    .replace(/_(.+?)_/gs,        '<em>$1</em>')            // _italic_
    .replace(/~~(.+?)~~/gs,      '<del>$1</del>')          // ~~strike~~
    .replace(/\|\|(.+?)\|\|/gs,  '<span class="chat-spoiler">$1</span>') // ||spoiler||
    .replace(/`(.+?)`/g,         '<code>$1</code>')        // `code`
    .replace(/<u>(.+?)<\/u>/gs,  '<u>$1</u>')              // <u> passthrough (already escaped → undo)

  // Restore <u> tags that were escaped
  s = s.replace(/&lt;u&gt;(.+?)&lt;\/u&gt;/gs, '<u>$1</u>')

  // Line breaks
  s = s.replace(/\n/g, '<br>')

  return s
}

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}
